// BOM Master — Feb 2026 restructure (v2, polished layout).
//
// Materials library. Each row = (Grade, Material Type, Density, Rate).
// Aluminium/Copper use a formula:
//   Total = (Base + Conversion + Packaging + Transport) × (1 − Discount%)
//   where Base = Nalco (Al) or LME (Cu).
// Every other material type uses a single manual Rate cell.

import { useState, useEffect, useMemo } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Layout from "../components/Layout";
import MasterExcelToolbar from "../components/MasterExcelToolbar";
import { Plus, Trash, PencilSimple, Stack } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const SPECIAL = new Set(["aluminium", "copper"]);
const baseLabel = (t) => t === "aluminium" ? "Nalco (₹/Kg)" : (t === "copper" ? "LME (₹/Kg)" : "Base Rate");

const blankForm = () => ({
  grade: "", material_type: "", density: "", rate: "",
  base_rate: "", conversion_rate: "", packaging: "", transport: "", discount_pct: "",
  scrap_rate: "",
});

function computeTotal(f) {
  const n = (x) => parseFloat(x || 0) || 0;
  if (SPECIAL.has(f.material_type)) {
    // Discount applies ONLY to (Base + Conversion). Packaging & transport
    // are logistics costs billed at par (never discounted).
    const discounted = (n(f.base_rate) + n(f.conversion_rate)) * (1 - n(f.discount_pct) / 100);
    return +(discounted + n(f.packaging) + n(f.transport)).toFixed(4);
  }
  return n(f.rate);
}

export default function BOMMaster() {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [b, t] = await Promise.all([
        axios.get(`${API}/bom-master`),
        axios.get(`${API}/material-types`),
      ]);
      setRows(b.data); setTypes(t.data);
    } catch (e) { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const buildPayload = (f) => {
    const n = (x) => x === "" || x == null ? null : parseFloat(x);
    return {
      grade: f.grade.trim(),
      material_type: f.material_type,
      density: n(f.density),
      rate: SPECIAL.has(f.material_type) ? null : n(f.rate),
      base_rate: SPECIAL.has(f.material_type) ? n(f.base_rate) : null,
      conversion_rate: SPECIAL.has(f.material_type) ? n(f.conversion_rate) : null,
      packaging: SPECIAL.has(f.material_type) ? n(f.packaging) : null,
      transport: SPECIAL.has(f.material_type) ? n(f.transport) : null,
      discount_pct: SPECIAL.has(f.material_type) ? n(f.discount_pct) : null,
      scrap_rate: n(f.scrap_rate),
    };
  };

  const openAdd = () => { setEditingId(null); setForm(blankForm()); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      grade: r.grade || "", material_type: r.material_type || "",
      density: r.density ?? "", rate: r.rate ?? "",
      base_rate: r.base_rate ?? "", conversion_rate: r.conversion_rate ?? "",
      packaging: r.packaging ?? "", transport: r.transport ?? "",
      discount_pct: r.discount_pct ?? "",
      scrap_rate: r.scrap_rate ?? "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.grade.trim() || !form.material_type) return toast.error("Grade and Material Type are required");
    try {
      if (editingId) {
        await axios.put(`${API}/bom-master/${editingId}`, buildPayload(form));
        toast.success("Material updated");
      } else {
        await axios.post(`${API}/bom-master`, buildPayload(form));
        toast.success("Material added");
      }
      setDialogOpen(false); setForm(blankForm()); setEditingId(null); loadAll();
    } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this material?")) return;
    try { await axios.delete(`${API}/bom-master/${id}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  const addType = async () => {
    if (!newTypeName.trim()) return;
    try {
      await axios.post(`${API}/material-types`, { name: newTypeName.trim() });
      toast.success("Material type added");
      setNewTypeName(""); setShowAddType(false);
      const t = await axios.get(`${API}/material-types`); setTypes(t.data);
    } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
  };

  const isSpec = SPECIAL.has(form.material_type);
  const previewTotal = useMemo(() => computeTotal(form), [form]);

  return (
    <Layout title="BOM Master">
      <div className="p-6 space-y-5 max-w-[1400px] mx-auto">
        <PageHeader
          icon={Stack}
          title="BOM Master"
          subtitle="Materials library. Aluminium & Copper use a Nalco/LME formula; other grades take a manual ₹/Kg rate."
          rightSlot={
            <div className="flex items-center gap-2 flex-wrap">
              <MasterExcelToolbar basePath="/bom-master" entityLabel="Materials" onImportDone={loadAll} size="sm" />
              <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white gap-1 h-9" onClick={openAdd} data-testid="bom-add-btn">
                <Plus size={14} /> Add Material
              </Button>
            </div>
          }
        />

        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-[#475569] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Type</th>
                  <th className="px-3 py-3 text-left font-semibold">Grade</th>
                  <th className="px-3 py-3 text-right font-semibold">Density</th>
                  <th className="px-3 py-3 text-right font-semibold">Base / Rate</th>
                  <th className="px-3 py-3 text-right font-semibold">Conv</th>
                  <th className="px-3 py-3 text-right font-semibold">Pkg</th>
                  <th className="px-3 py-3 text-right font-semibold">Trans</th>
                  <th className="px-3 py-3 text-right font-semibold">Disc %</th>
                  <th className="px-3 py-3 text-right font-semibold">Scrap Rate</th>
                  <th className="px-3 py-3 text-right font-semibold bg-[#F0FDF4] text-[#166534]">Total (₹/Kg)</th>
                  <th className="px-3 py-3 text-center font-semibold w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={11} className="py-10 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={11} className="py-12 text-center text-[#94A3B8]">No materials yet — click <b>Add Material</b> to create your first one.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-[#F8FAFC]">
                    <td className="px-4 py-3 text-[#334155] font-semibold uppercase text-xs">{types.find(t => t.code === r.material_type)?.name || r.material_type}</td>
                    <td className="px-3 py-3 font-mono font-bold text-[#0F172A]">{r.grade}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#334155]">{r.density ?? "—"}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#334155]">{SPECIAL.has(r.material_type) ? (r.base_rate ?? "—") : (r.rate ?? "—")}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#64748B]">{r.conversion_rate ?? "—"}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#64748B]">{r.packaging ?? "—"}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#64748B]">{r.transport ?? "—"}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#64748B]">{r.discount_pct != null ? r.discount_pct + "%" : "—"}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#64748B]">{r.scrap_rate != null ? "₹" + r.scrap_rate : "—"}</td>
                    <td className="px-3 py-3 text-right font-mono font-black text-[#166534] bg-[#F0FDF4]">₹{typeof (r.total ?? r.rate ?? 0).toFixed === "function" ? (r.total ?? r.rate ?? 0).toFixed(2) : (r.total ?? r.rate ?? 0)}</td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><PencilSimple size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-[#DC2626]"><Trash size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <p className="text-[11px] text-[#94A3B8]">
          Aluminium &amp; Copper Total = <span className="font-mono">((Base + Conv) × (1 − Discount%)) + Packaging + Transport</span>.
          Other materials use the manual Rate cell as-is.
        </p>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit Material" : "Add Material"}</DialogTitle></DialogHeader>

          <div className="space-y-4 pt-2">
            <FormSection title="Material">
              <FieldGrid cols={3}>
                <Field label="Grade *">
                  <Input value={form.grade} onChange={e => setForm({ ...form, grade: e.target.value })}
                    placeholder="e.g. EN8, 6061, C110" data-testid="bom-grade" />
                </Field>
                <Field label="Type of Material *">
                  <div className="flex gap-1">
                    <SelectPlain value={form.material_type}
                      onChange={v => setForm({ ...form, material_type: v })}
                      options={[{ code: "", label: "— Select —" }, ...types.map(t => ({ code: t.code, label: t.name }))]}
                      testId="bom-material-type" />
                    <Button size="sm" variant="outline" onClick={() => setShowAddType(true)} title="Add new material type" data-testid="bom-add-type">
                      <Plus size={14} />
                    </Button>
                  </div>
                </Field>
                <Field label="Density (Kg/cm³)">
                  <Input type="number" step="0.001" value={form.density}
                    onChange={e => setForm({ ...form, density: e.target.value })}
                    data-testid="bom-density" />
                </Field>
              </FieldGrid>
              <div className="mt-3">
                <FieldGrid cols={3}>
                  <Field label="Scrap Rate (₹/Kg)"
                    hint={isSpec
                      ? `Auto — 40% of ${form.material_type === "aluminium" ? "Nalco" : "LME"} base rate. Not editable.`
                      : "Sell-back rate for scrap of this grade — used in Cost Sheet."}>
                    <Input type="number" step="0.01"
                      value={isSpec ? Number(((parseFloat(form.base_rate) || 0) * 0.40).toFixed(4)) : form.scrap_rate}
                      onChange={e => setForm({ ...form, scrap_rate: e.target.value })}
                      readOnly={isSpec}
                      className={isSpec ? "bg-[#F1F5F9]" : ""}
                      data-testid="bom-scrap-rate" />
                  </Field>
                </FieldGrid>
              </div>
            </FormSection>

            {!isSpec && (
              <FormSection title="Rate">
                <FieldGrid cols={3}>
                  <Field label="Rate (₹/Kg) *">
                    <Input type="number" step="0.01" value={form.rate}
                      onChange={e => setForm({ ...form, rate: e.target.value })}
                      data-testid="bom-rate-manual" />
                  </Field>
                </FieldGrid>
              </FormSection>
            )}

            {isSpec && (
              <FormSection
                title={`${form.material_type === "aluminium" ? "Aluminium" : "Copper"} Formula`}
                hint="Total = ((Base + Conv) × (1 − Disc%)) + Pkg + Trans">
                <FieldGrid cols={6}>
                  <Field label={baseLabel(form.material_type)}>
                    <Input type="number" step="0.01" value={form.base_rate}
                      onChange={e => setForm({ ...form, base_rate: e.target.value })}
                      data-testid="bom-base-rate" />
                  </Field>
                  <Field label="Conversion">
                    <Input type="number" step="0.01" value={form.conversion_rate}
                      onChange={e => setForm({ ...form, conversion_rate: e.target.value })}
                      data-testid="bom-conv-rate" />
                  </Field>
                  <Field label="Packaging">
                    <Input type="number" step="0.01" value={form.packaging}
                      onChange={e => setForm({ ...form, packaging: e.target.value })}
                      data-testid="bom-pkg" />
                  </Field>
                  <Field label="Transport">
                    <Input type="number" step="0.01" value={form.transport}
                      onChange={e => setForm({ ...form, transport: e.target.value })}
                      data-testid="bom-transport" />
                  </Field>
                  <Field label="Discount %">
                    <Input type="number" step="0.01" value={form.discount_pct}
                      onChange={e => setForm({ ...form, discount_pct: e.target.value })}
                      data-testid="bom-discount" />
                  </Field>
                  <Field label="Total (₹/Kg)">
                    <div className="min-h-[38px] flex items-center justify-end font-mono font-bold text-[#166534] px-3 border border-[#BBF7D0] bg-[#F0FDF4] rounded" data-testid="bom-total-calc">
                      ₹{previewTotal.toFixed(2)}
                    </div>
                  </Field>
                </FieldGrid>
              </FormSection>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#E2E8F0] mt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#1D2951] text-white" onClick={save} data-testid="bom-save">
              <Plus size={14} /> {editingId ? "Save Changes" : "Add Material"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Add Material Type mini-modal (nested Dialog so it stacks ABOVE
             the primary Add/Edit modal and is only dismissible via X). ── */}
      <Dialog open={showAddType} onOpenChange={setShowAddType}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[#1D2951]">Add Material Type</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div>
              <Label className="text-xs font-semibold">Name</Label>
              <Input value={newTypeName} onChange={e => setNewTypeName(e.target.value)}
                placeholder="e.g. Cast Iron, Titanium…" data-testid="new-type-name" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddType(false)}>Cancel</Button>
              <Button className="bg-[#1D2951] text-white" onClick={addType} data-testid="save-new-type">Add</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/* Local layout primitives (mirrored from ProcessMaster). */
function PageHeader({ icon: Icon, title, subtitle, rightSlot }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-3">
        {Icon && <div className="p-2 rounded-lg bg-[#1D2951]/5 text-[#1D2951]"><Icon size={20} weight="fill" /></div>}
        <div>
          <h1 className="text-2xl font-black tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>{title}</h1>
          {subtitle && <p className="text-xs text-[#64748B] mt-0.5 max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      {rightSlot}
    </div>
  );
}
function FormSection({ title, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-[10px] font-bold uppercase tracking-widest text-[#64748B]">{title}</h3>
        {hint && <span className="text-[10px] text-[#94A3B8] italic">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
function FieldGrid({ cols = 3, children }) {
  const grid = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4", 6: "grid-cols-6" }[cols] || "grid-cols-3";
  return <div className={`grid ${grid} gap-3`}>{children}</div>;
}
function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold text-[#334155] flex items-center gap-1">
        {label}{hint && <span className="text-[10px] font-normal text-[#94A3B8]">({hint})</span>}
      </Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
function SelectPlain({ value, onChange, options, testId }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="flex-1 min-h-[38px] border border-[#E2E8F0] rounded px-2 text-xs bg-white"
      data-testid={testId}>
      {options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
    </select>
  );
}
